import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { employeeService } from '../services/api';
import {
  getTimesheetsByDate,
  createTimesheet,
  updateTimesheet,
} from '../services/timesheetService';
import * as XLSX from 'xlsx';
import './BulkTimesheetEditor.css';

const BulkTimesheetEditor = ({ date: initialDate, onClose, onSave }) => {
  const { hasModulePermission } = useAuth();
  const canCreate = hasModulePermission('TIMESHEETS', 'create');
  const canUpdate = hasModulePermission('TIMESHEETS', 'update');

  // État local pour la date (utilise la prop initiale ou la date du jour)
  const [date, setDate] = useState(initialDate || new Date().toISOString().split('T')[0]);
  
  const [employees, setEmployees] = useState([]);
  const [timesheets, setTimesheets] = useState({}); // { employeeId: timesheet }
  const [editingCell, setEditingCell] = useState(null); // { employeeId, field }
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [defaultCheckIn, setDefaultCheckIn] = useState('08:00');
  const [defaultCheckOut, setDefaultCheckOut] = useState('17:00');
  const [defaultBreak, setDefaultBreak] = useState(60);
  const [selectedEmployees, setSelectedEmployees] = useState(new Set());
  const [filterQuery, setFilterQuery] = useState('');

  const tableRef = useRef(null);
  const inputRef = useRef(null);

  // Charger les employés actifs
  useEffect(() => {
    loadEmployees();
  }, []);

  // Charger les pointages existants pour la date
  useEffect(() => {
    if (date) {
      loadTimesheets();
      // Réinitialiser les pointages quand la date change
      setTimesheets({});
      setSelectedEmployees(new Set());
    }
  }, [date]);

  // Gestion des raccourcis clavier
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+S : Sauvegarder
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        handleSaveAll();
      }
      // Ctrl+A : Sélectionner tous
      if (e.ctrlKey && e.key === 'a' && !editingCell) {
        e.preventDefault();
        setSelectedEmployees(new Set(employees.map(emp => emp.id)));
      }
      // Ctrl+D : Appliquer les valeurs par défaut aux sélectionnés
      if (e.ctrlKey && e.key === 'd' && !editingCell) {
        e.preventDefault();
        applyDefaultsToSelected();
      }
      // Ctrl+Shift+A : Marquer tous comme absents
      if (e.ctrlKey && e.shiftKey && e.key === 'A' && !editingCell) {
        e.preventDefault();
        markSelectedAsAbsent();
      }
      // Échap : Annuler l'édition
      if (e.key === 'Escape' && editingCell) {
        setEditingCell(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editingCell, employees, selectedEmployees]);

  const loadEmployees = async () => {
    try {
      const res = await employeeService.getAll();
      const activeEmployees = (res.data || []).filter(emp => emp.statutActif !== false);
      setEmployees(activeEmployees);
    } catch (err) {
      setError('Erreur lors du chargement des employés');
      console.error(err);
    }
  };

  const loadTimesheets = async () => {
    if (!date) return;
    setLoading(true);
    try {
      const res = await getTimesheetsByDate(date);
      const existingTimesheets = {};
      (res.data || []).forEach(ts => {
        existingTimesheets[ts.employeeId] = ts;
      });
      setTimesheets(existingTimesheets);
    } catch (err) {
      setError('Erreur lors du chargement des pointages');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getTimesheetForEmployee = (employeeId) => {
    return timesheets[employeeId] || {
      employeeId,
      date,
      checkInTime: '',
      checkOutTime: '',
      breakDurationMinutes: 0,
      isAbsent: false,
      notes: '',
    };
  };

  const updateTimesheetField = (employeeId, field, value) => {
    setTimesheets(prev => {
      const current = prev[employeeId] || { employeeId, date };
      return {
        ...prev,
        [employeeId]: {
          ...current,
          [field]: value,
          // Si on marque comme absent, vider les heures
          ...(field === 'isAbsent' && value === true ? {
            checkInTime: '',
            checkOutTime: '',
            breakDurationMinutes: 0,
            notes: current.notes || 'Absence',
          } : {}),
        },
      };
    });
  };

  const handleCellClick = (employeeId, field) => {
    if (!canCreate && !canUpdate) return;
    setEditingCell({ employeeId, field });
  };

  const handleCellBlur = () => {
    setTimeout(() => setEditingCell(null), 200);
  };

  const handleCellKeyDown = (e, employeeId, field) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      // Navigation vers la cellule suivante
      const currentIndex = employees.findIndex(emp => emp.id === employeeId);
      if (e.key === 'Tab' && !e.shiftKey) {
        // Tab : cellule suivante
        if (field === 'checkInTime') {
          setEditingCell({ employeeId, field: 'checkOutTime' });
        } else if (field === 'checkOutTime') {
          setEditingCell({ employeeId, field: 'breakDurationMinutes' });
        } else if (currentIndex < employees.length - 1) {
          setEditingCell({ employeeId: employees[currentIndex + 1].id, field: 'checkInTime' });
        }
      } else if (e.key === 'Tab' && e.shiftKey) {
        // Shift+Tab : cellule précédente
        if (field === 'checkOutTime') {
          setEditingCell({ employeeId, field: 'checkInTime' });
        } else if (field === 'breakDurationMinutes') {
          setEditingCell({ employeeId, field: 'checkOutTime' });
        } else if (currentIndex > 0) {
          setEditingCell({ employeeId: employees[currentIndex - 1].id, field: 'breakDurationMinutes' });
        }
      } else {
        // Enter : ligne suivante, même champ
        if (currentIndex < employees.length - 1) {
          setEditingCell({ employeeId: employees[currentIndex + 1].id, field });
        }
      }
    }
  };

  const applyDefaultsToSelected = () => {
    if (selectedEmployees.size === 0) {
      // Appliquer à tous si aucun sélectionné
      employees.forEach(emp => {
        const ts = getTimesheetForEmployee(emp.id);
        if (!ts.isAbsent && !ts.id) {
          updateTimesheetField(emp.id, 'checkInTime', defaultCheckIn);
          updateTimesheetField(emp.id, 'checkOutTime', defaultCheckOut);
          updateTimesheetField(emp.id, 'breakDurationMinutes', defaultBreak);
        }
      });
    } else {
      selectedEmployees.forEach(employeeId => {
        const ts = getTimesheetForEmployee(employeeId);
        if (!ts.isAbsent && !ts.id) {
          updateTimesheetField(employeeId, 'checkInTime', defaultCheckIn);
          updateTimesheetField(employeeId, 'checkOutTime', defaultCheckOut);
          updateTimesheetField(employeeId, 'breakDurationMinutes', defaultBreak);
        }
      });
    }
    setSuccess('Valeurs par défaut appliquées');
    setTimeout(() => setSuccess(''), 3000);
  };

  const markSelectedAsAbsent = () => {
    if (selectedEmployees.size === 0) {
      employees.forEach(emp => {
        updateTimesheetField(emp.id, 'isAbsent', true);
      });
    } else {
      selectedEmployees.forEach(employeeId => {
        updateTimesheetField(employeeId, 'isAbsent', true);
      });
    }
    setSuccess('Employés marqués comme absents');
    setTimeout(() => setSuccess(''), 3000);
  };

  const toggleEmployeeSelection = (employeeId) => {
    setSelectedEmployees(prev => {
      const next = new Set(prev);
      if (next.has(employeeId)) {
        next.delete(employeeId);
      } else {
        next.add(employeeId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    const filtered = getFilteredEmployees();
    if (selectedEmployees.size === filtered.length) {
      setSelectedEmployees(new Set());
    } else {
      setSelectedEmployees(new Set(filtered.map(emp => emp.id)));
    }
  };

  const handleSaveAll = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const timesheetsToSave = Object.values(timesheets).filter(ts => {
        // Sauvegarder si modifié ou si nouveau avec des données
        return ts.checkInTime || ts.checkOutTime || ts.isAbsent || ts.notes;
      });

      if (timesheetsToSave.length === 0) {
        setError('Aucun pointage à sauvegarder');
        setSaving(false);
        return;
      }

      const promises = timesheetsToSave.map(ts => {
        const payload = {
          employeeId: ts.employeeId,
          date: date, // Toujours utiliser la date locale actuelle
          checkInTime: ts.checkInTime || '',
          checkOutTime: ts.checkOutTime || '',
          breakDurationMinutes: ts.breakDurationMinutes || 0,
          isAbsent: ts.isAbsent || false,
          notes: ts.notes || '',
        };

        if (ts.id) {
          return updateTimesheet(ts.id, payload);
        } else {
          return createTimesheet(payload);
        }
      });

      await Promise.all(promises);
      setSuccess(`${timesheetsToSave.length} pointage(s) sauvegardé(s) avec succès`);
      setTimeout(() => {
        if (onSave) onSave();
        if (onClose) onClose();
      }, 1500);
    } catch (err) {
      setError('Erreur lors de la sauvegarde : ' + (err.response?.data?.message || err.message));
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleImportExcel = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const workbook = XLSX.read(event.target.result, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const data = XLSX.utils.sheet_to_json(worksheet);

        const importedTimesheets = {};
        const errors = [];

        data.forEach((row, index) => {
          try {
            // Chercher l'employé par matricule ou nom
            const matricule = row['Matricule'] || row['matricule'] || '';
            const nom = row['Nom'] || row['nom'] || '';
            const prenom = row['Prénom'] || row['prenom'] || '';

            let employee = null;
            if (matricule) {
              employee = employees.find(emp => emp.matricule === String(matricule));
            }
            if (!employee && nom && prenom) {
              employee = employees.find(emp =>
                emp.nom.toLowerCase() === nom.toLowerCase() &&
                emp.prenom.toLowerCase() === prenom.toLowerCase()
              );
            }

            if (!employee) {
              errors.push(`Ligne ${index + 2}: Employé non trouvé (${matricule || nom + ' ' + prenom})`);
              return;
            }

            const checkIn = row['Heure entrée'] || row['Heure entree'] || row['checkInTime'] || '';
            const checkOut = row['Heure sortie'] || row['checkOutTime'] || '';
            const breakDuration = parseInt(row['Pause (min)'] || row['pause'] || row['breakDurationMinutes'] || 0);
            const isAbsent = row['Absent'] === 'Oui' || row['Absent'] === 'oui' || row['Absent'] === true || row['isAbsent'] === true;

            importedTimesheets[employee.id] = {
              employeeId: employee.id,
              date,
              checkInTime: checkIn,
              checkOutTime: checkOut,
              breakDurationMinutes: breakDuration,
              isAbsent,
              notes: row['Notes'] || row['notes'] || '',
            };
          } catch (err) {
            errors.push(`Ligne ${index + 2}: ${err.message}`);
          }
        });

        if (errors.length > 0) {
          setError(`Erreurs lors de l'import : ${errors.join('; ')}`);
        }

        setTimesheets(prev => ({ ...prev, ...importedTimesheets }));
        setSuccess(`${Object.keys(importedTimesheets).length} pointage(s) importé(s)`);
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError('Erreur lors de la lecture du fichier Excel');
        console.error(err);
      }
    };

    reader.readAsBinaryString(file);
    e.target.value = ''; // Réinitialiser l'input
  };

  const handleExportTemplate = () => {
    const templateData = employees.map(emp => ({
      'Matricule': emp.matricule || '',
      'Nom': emp.nom || '',
      'Prénom': emp.prenom || '',
      'Heure entrée': '',
      'Heure sortie': '',
      'Pause (min)': '',
      'Absent': '',
      'Notes': '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Pointages');
    const dateStr = date ? date.replace(/-/g, '') : 'date';
    XLSX.writeFile(workbook, `template_pointage_${dateStr}.xlsx`);
  };

  const getFilteredEmployees = () => {
    if (!filterQuery) return employees;
    const query = filterQuery.toLowerCase();
    return employees.filter(emp =>
      `${emp.nom} ${emp.prenom}`.toLowerCase().includes(query) ||
      (emp.matricule && emp.matricule.toLowerCase().includes(query))
    );
  };

  const filteredEmployees = getFilteredEmployees();

  if (!canCreate && !canUpdate) {
    return <div className="error">Vous n'avez pas les permissions nécessaires</div>;
  }

  return (
    <div className="bulk-timesheet-editor">
      <div className="bulk-editor-header">
        <div className="header-left">
          <h2>Pointage en masse - {date ? new Date(date).toLocaleDateString('fr-FR') : 'Date non sélectionnée'}</h2>
          <div className="shortcuts-info">
            <span className="shortcut-hint">Raccourcis: Ctrl+S (sauvegarder), Ctrl+A (sélectionner), Ctrl+D (appliquer défaut), Ctrl+Shift+A (marquer absent)</span>
          </div>
        </div>
        <div className="header-right">
          <button className="btn btn-secondary" onClick={handleExportTemplate}>
            📥 Télécharger template Excel
          </button>
          <label className="btn btn-secondary">
            📤 Importer Excel
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleImportExcel}
              style={{ display: 'none' }}
            />
          </label>
          <button className="btn btn-primary" onClick={handleSaveAll} disabled={saving}>
            {saving ? 'Sauvegarde...' : '💾 Sauvegarder tout (Ctrl+S)'}
          </button>
          {onClose && (
            <button className="btn btn-secondary" onClick={onClose}>
              Fermer
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <span>{error}</span>
          <button onClick={() => setError('')}>×</button>
        </div>
      )}

      {success && (
        <div className="success-banner">
          <span>{success}</span>
        </div>
      )}

      <div className="bulk-editor-controls">
        <div className="date-selection">
          <label>
            Date du pointage:
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="date-input"
            />
          </label>
        </div>

        <div className="default-values">
          <label>
            Heure entrée par défaut:
            <input
              type="time"
              value={defaultCheckIn}
              onChange={(e) => setDefaultCheckIn(e.target.value)}
            />
          </label>
          <label>
            Heure sortie par défaut:
            <input
              type="time"
              value={defaultCheckOut}
              onChange={(e) => setDefaultCheckOut(e.target.value)}
            />
          </label>
          <label>
            Pause (min) par défaut:
            <input
              type="number"
              value={defaultBreak}
              onChange={(e) => setDefaultBreak(parseInt(e.target.value) || 0)}
              min="0"
            />
          </label>
          <button className="btn btn-sm" onClick={applyDefaultsToSelected}>
            Appliquer aux sélectionnés (Ctrl+D)
          </button>
        </div>

        <div className="search-filter">
          <input
            type="text"
            placeholder="Rechercher un employé..."
            value={filterQuery}
            onChange={(e) => setFilterQuery(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {loading ? (
        <div className="loading">Chargement...</div>
      ) : (
        <div className="bulk-editor-table-wrapper">
          <table className="bulk-editor-table" ref={tableRef}>
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={selectedEmployees.size === filteredEmployees.length && filteredEmployees.length > 0}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th>Matricule</th>
                <th>Nom</th>
                <th>Prénom</th>
                <th>Absent</th>
                <th>Heure entrée</th>
                <th>Heure sortie</th>
                <th>Pause (min)</th>
                <th>Notes</th>
                <th>Statut</th>
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.map(emp => {
                const ts = getTimesheetForEmployee(emp.id);
                const isSelected = selectedEmployees.has(emp.id);
                const isEditing = editingCell?.employeeId === emp.id;

                return (
                  <tr
                    key={emp.id}
                    className={`${isSelected ? 'selected' : ''} ${ts.isAbsent ? 'absent' : ''} ${ts.id ? 'existing' : ''}`}
                  >
                    <td>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleEmployeeSelection(emp.id)}
                      />
                    </td>
                    <td>{emp.matricule || '-'}</td>
                    <td>{emp.nom}</td>
                    <td>{emp.prenom}</td>
                    <td>
                      <input
                        type="checkbox"
                        checked={ts.isAbsent || false}
                        onChange={(e) => updateTimesheetField(emp.id, 'isAbsent', e.target.checked)}
                      />
                    </td>
                    <td
                      className={`editable-cell ${isEditing && editingCell.field === 'checkInTime' ? 'editing' : ''}`}
                      onClick={() => handleCellClick(emp.id, 'checkInTime')}
                    >
                      {isEditing && editingCell.field === 'checkInTime' ? (
                        <input
                          ref={inputRef}
                          type="time"
                          value={ts.checkInTime || ''}
                          onChange={(e) => updateTimesheetField(emp.id, 'checkInTime', e.target.value)}
                          onBlur={handleCellBlur}
                          onKeyDown={(e) => handleCellKeyDown(e, emp.id, 'checkInTime')}
                          disabled={ts.isAbsent}
                          autoFocus
                        />
                      ) : (
                        <span>{ts.checkInTime || '-'}</span>
                      )}
                    </td>
                    <td
                      className={`editable-cell ${isEditing && editingCell.field === 'checkOutTime' ? 'editing' : ''}`}
                      onClick={() => handleCellClick(emp.id, 'checkOutTime')}
                    >
                      {isEditing && editingCell.field === 'checkOutTime' ? (
                        <input
                          ref={inputRef}
                          type="time"
                          value={ts.checkOutTime || ''}
                          onChange={(e) => updateTimesheetField(emp.id, 'checkOutTime', e.target.value)}
                          onBlur={handleCellBlur}
                          onKeyDown={(e) => handleCellKeyDown(e, emp.id, 'checkOutTime')}
                          disabled={ts.isAbsent}
                          autoFocus
                        />
                      ) : (
                        <span>{ts.checkOutTime || '-'}</span>
                      )}
                    </td>
                    <td
                      className={`editable-cell ${isEditing && editingCell.field === 'breakDurationMinutes' ? 'editing' : ''}`}
                      onClick={() => handleCellClick(emp.id, 'breakDurationMinutes')}
                    >
                      {isEditing && editingCell.field === 'breakDurationMinutes' ? (
                        <input
                          ref={inputRef}
                          type="number"
                          value={ts.breakDurationMinutes || 0}
                          onChange={(e) => updateTimesheetField(emp.id, 'breakDurationMinutes', parseInt(e.target.value) || 0)}
                          onBlur={handleCellBlur}
                          onKeyDown={(e) => handleCellKeyDown(e, emp.id, 'breakDurationMinutes')}
                          disabled={ts.isAbsent}
                          min="0"
                          autoFocus
                        />
                      ) : (
                        <span>{ts.breakDurationMinutes || 0}</span>
                      )}
                    </td>
                    <td
                      className={`editable-cell ${isEditing && editingCell.field === 'notes' ? 'editing' : ''}`}
                      onClick={() => handleCellClick(emp.id, 'notes')}
                    >
                      {isEditing && editingCell.field === 'notes' ? (
                        <input
                          ref={inputRef}
                          type="text"
                          value={ts.notes || ''}
                          onChange={(e) => updateTimesheetField(emp.id, 'notes', e.target.value)}
                          onBlur={handleCellBlur}
                          onKeyDown={(e) => handleCellKeyDown(e, emp.id, 'notes')}
                          autoFocus
                        />
                      ) : (
                        <span className="notes-cell">{ts.notes || '-'}</span>
                      )}
                    </td>
                    <td>
                      {ts.id ? (
                        <span className="status-badge existing">Existant</span>
                      ) : ts.checkInTime || ts.checkOutTime || ts.isAbsent ? (
                        <span className="status-badge new">Nouveau</span>
                      ) : (
                        <span className="status-badge empty">Vide</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="bulk-editor-footer">
        <div className="stats">
          <span>Total: {filteredEmployees.length} employé(s)</span>
          <span>Sélectionnés: {selectedEmployees.size}</span>
          <span>
            À sauvegarder: {Object.values(timesheets).filter(ts => ts.checkInTime || ts.checkOutTime || ts.isAbsent || ts.notes).length}
          </span>
        </div>
      </div>
    </div>
  );
};

export default BulkTimesheetEditor;

